import dayjs from 'dayjs';

import { dashesOrNum } from '@cast/utils';
import type { ConvertableToDateTime } from '@cast/shared-types';
import type { CostReportResourceUsageNormalized } from '@cast/shared-cost-data-access';

export type ResourceUtilizationDatapoint = {
  timestamp: string;
  provisioned: number | null;
  requested: number | null;
  used: number | null;
};

export type ResourceChartDatapoints = {
  cpu: { datapoints: ResourceUtilizationDatapoint[]; hasNoData: boolean };
  memory: { datapoints: ResourceUtilizationDatapoint[]; hasNoData: boolean };
  gpu: {
    datapoints: Array<{
      timestamp: string;
      gpuMemoryUsed: number | null;
      gpuUsed: number | null;
      gpuRequested: number | null;
    }>;
    hasNoData: boolean;
  };
  storage: {
    datapoints: Array<{
      timestamp: string;
      provisioned: number | null;
      requested: number | null;
    }>;
    hasNoData: boolean;
  };
};

export const mapUsageToResourceChartDatapoints = (
  usage: CostReportResourceUsageNormalized
): ResourceChartDatapoints =>
  usage.reduce(
    (acc, item) => {
      const {
        cpuProvisioned,
        cpuRequested,
        cpuUsed,
        ramProvisioned,
        ramRequested,
        ramUsed,
        gpuUsed,
        gpuMemoryUsed,
        gpuRequested,
        storageProvisioned,
        storageRequested,
      } = item;

      const cpuProvisionedValue = cpuProvisioned?.toNumber() ?? null;
      const cpuRequestedValue = cpuRequested?.toNumber() ?? null;
      const cpuUsedValue = cpuUsed?.toNumber() ?? null;
      const hasNoCpuData =
        cpuProvisionedValue == null &&
        cpuRequestedValue == null &&
        cpuUsedValue == null;

      if (acc.cpu.hasNoData && !hasNoCpuData) {
        acc.cpu.hasNoData = false;
      }

      acc.cpu.datapoints.push({
        timestamp: item.timestamp,
        provisioned: cpuProvisionedValue,
        requested: cpuRequestedValue,
        used: cpuUsedValue,
      });

      const ramProvisionedValue = ramProvisioned?.toNumber() ?? null;
      const ramRequestedValue = ramRequested?.toNumber() ?? null;
      const ramUsedValue = ramUsed?.toNumber() ?? null;
      const hasNoMemoryData =
        ramProvisionedValue == null &&
        ramRequestedValue == null &&
        ramUsedValue == null;

      if (acc.memory.hasNoData && !hasNoMemoryData) {
        acc.memory.hasNoData = false;
      }

      acc.memory.datapoints.push({
        timestamp: item.timestamp,
        provisioned: ramProvisionedValue,
        requested: ramRequestedValue,
        used: ramUsedValue,
      });

      const gpuUsedValue = gpuUsed?.toNumber() ?? null;
      const gpuMemoryUsedValue = gpuMemoryUsed?.toNumber() ?? null;
      const gpuRequestedValue = gpuRequested?.toNumber() ?? null;
      const hasNoGpuData = gpuMemoryUsedValue == null && gpuUsedValue == null;

      if (acc.gpu.hasNoData && !hasNoGpuData) {
        acc.gpu.hasNoData = false;
      }

      acc.gpu.datapoints.push({
        timestamp: item.timestamp,
        gpuMemoryUsed: gpuMemoryUsedValue,
        gpuUsed: gpuUsedValue,
        gpuRequested: gpuRequestedValue,
      });

      const storageProvisionedValue = storageProvisioned?.toNumber() ?? null;
      const storageRequestedValue = storageRequested?.toNumber() ?? null;
      const hasNoStorageData =
        storageProvisionedValue == null && storageRequestedValue == null;

      if (acc.storage.hasNoData && !hasNoStorageData) {
        acc.storage.hasNoData = false;
      }

      acc.storage.datapoints.push({
        timestamp: item.timestamp,
        provisioned: storageProvisionedValue,
        requested: storageRequestedValue,
      });

      return acc;
    },
    {
      cpu: { datapoints: [], hasNoData: true },
      memory: { datapoints: [], hasNoData: true },
      gpu: { datapoints: [], hasNoData: true },
      storage: { datapoints: [], hasNoData: true },
    } as ResourceChartDatapoints
  );

export const createResourceUsageFiller =
  () =>
  (
    timestamp: ConvertableToDateTime
  ): CostReportResourceUsageNormalized[number] => ({
    timestamp: dayjs(timestamp).toISOString(),
    cpuProvisioned: null,
    ramProvisioned: null,
    cpuRequested: null,
    ramRequested: null,
    cpuUsed: null,
    ramUsed: null,
    gpuAllocatable: null,
    gpuRequested: null,
    gpuUsed: null,
    gpuMemoryUsed: null,
    tpuAllocatable: null,
    tpuRequested: null,
    storageProvisioned: null,
    storageRequested: null,
    storageClaimed: null,
    measurableGpuPodCount: null,
    totalGpuPodCount: null,
  });

export const averageResourceValues = (
  datapoints: ResourceUtilizationDatapoint[]
): {
  provisioned: string;
  requested: string;
  used: string;
  utilization: string;
} => {
  const avg = (values: Array<number | null>) => {
    const filtered = values.filter((v): v is number => v != null);
    if (!filtered.length) {
      return null;
    }
    return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
  };

  const provisioned = avg(datapoints.map((d) => d.provisioned));
  const requested = avg(datapoints.map((d) => d.requested));
  const used = avg(datapoints.map((d) => d.used));
  const utilization =
    provisioned && used != null ? (used / provisioned) * 100 : null;

  const fmt = (value: number | null, roundBy = 1) =>
    value == null
      ? '—'
      : String(dashesOrNum({ value, roundBy, decimals: roundBy }));

  return {
    provisioned: fmt(provisioned),
    requested: fmt(requested),
    used: fmt(used),
    utilization: fmt(utilization),
  };
};

export const mapResourceDatapointsToChartPoints = (
  datapoints: ResourceUtilizationDatapoint[]
) =>
  datapoints.map((point) => {
    const timestamp = point.timestamp;
    const chartTimestampUnix = dayjs(timestamp).unix();
    const { provisioned, requested, used } = point;
    const utilization =
      provisioned != null && used != null && provisioned > 0
        ? (used / provisioned) * 100
        : null;

    return {
      timestamp,
      chartTimestampUnix,
      provisioned,
      requested,
      used,
      utilization,
    };
  });
